import { notify } from "@/components/common/Notification";
import { isAuthGateBlockedError } from "@/lib/authBootstrapGate";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { axiosConfig } from "./axiosConfig";

export interface ApiError {
  message: string;
  status: number;
  errors?: any;
}

export interface RequestConfig extends AxiosRequestConfig {
  showNotification?: boolean;
  customErrorMessage?: string;
}

export const get = async <T = any>(
  url: string,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.get(url, config);
    return response.data;
  } catch (error: any) {
    handleRequestError(error, config);
    throw error;
  }
};

export const post = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.post(
      url,
      data,
      config
    );
    return response.data;
  } catch (error: any) {
    handleRequestError(error, config);
    throw error;
  }
};

export const put = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.put(url, data, config);
    return response.data;
  } catch (error: any) {
    handleRequestError(error, config);
    throw error;
  }
};

export const patch = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.patch(
      url,
      data,
      config
    );
    return response.data;
  } catch (error: any) {
    handleRequestError(error, config);
    throw error;
  }
};

export const del = async <T = any>(
  url: string,
  config?: RequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosConfig.delete(url, config);
    return response.data;
  } catch (error: any) {
    handleRequestError(error, config);
    throw error;
  }
};

/** Shared axios error → notify (for callers that use axiosConfig directly). */
export const handleRequestError = (error: any, config?: RequestConfig) => {
  if (isAuthGateBlockedError(error)) {
    return;
  }
  if (config?.showNotification === false) {
    return;
  }
  if (
    error.code === "ERR_NETWORK" ||
    error.message?.includes("Network Error")
  ) {
    notify.error(
      "Network Error",
      "Unable to connect to the server. Please check your internet connection."
    );
    return;
  }

  const { status, data } = error.response || {};
  if (!status) return;

  const errorMessage =
    config?.customErrorMessage ??
    data?.error?.message ??
    data?.message ??
    data?.error ??
    data?.non_field_errors ??
    "An error occurred";
  const errorCode = data?.error?.code ?? status;
  if (status === 500) {
    notify.error(errorCode, "Bad Gateway");
  } else {
    notify.error(errorMessage);
  }
};
